import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { useTranslation } from 'react-i18next';

import {
  useTemplatesForEditQuery,
  useUpsertTemplateOverrideMutation,
  useDeleteTemplateOverrideMutation,
  type TemplateForEdit,
  type TemplateCategory,
} from '@/templates/hooks';
import { templateDisplayName } from '@/templates/displayName';
import { ErrorAlert } from '@/components/ErrorAlert';

const { Text } = Typography;
const { TextArea } = Input;

type CategoryFilter = 'all' | TemplateCategory;

function CategoryTag({ category }: { category: TemplateCategory }): JSX.Element {
  const { t } = useTranslation();
  if (category === 'marketing') {
    return <Tag color="orange">{t('templates.category.marketing')}</Tag>;
  }
  return <Tag color="blue">{t('templates.category.transactional')}</Tag>;
}

// Бейдж состояния строки: «Изменён» (override оптики) / «Системный» (заготовка).
function StateTag({ overridden }: { overridden: boolean }): JSX.Element {
  const { t } = useTranslation();
  if (overridden) {
    return <Tag color="cyan">{t('templates.state.overridden')}</Tag>;
  }
  return <Tag>{t('templates.state.system')}</Tag>;
}

// Поиск неизвестных плейсхолдеров `{...}` (одинарные скобки — реальный формат)
// среди текста, которых нет в списке `variables`. Не блокирует сохранение —
// только мягкое предупреждение.
function unknownVars(text: string, allowed: string[]): string[] {
  const found = text.match(/\{[^{}]+\}/g) ?? [];
  const bad: string[] = [];
  for (const token of found) {
    const name = token.slice(1, -1).trim();
    if (name && !allowed.includes(name) && !bad.includes(name)) bad.push(name);
  }
  return bad;
}

// Read-only блок системной заготовки (ru/kk) — ориентир для оптики.
function SystemBodyBlock({ template }: { template: TemplateForEdit }): JSX.Element {
  const { t } = useTranslation();
  const rows: Array<{ label: string; body: string | undefined }> = [
    { label: t('templates.editor.body_ru'), body: template.system_bodies['ru'] },
    { label: t('templates.editor.body_kk'), body: template.system_bodies['kk'] },
  ];
  return (
    <Card size="small" title={t('templates.editor.system_title')}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {rows.map((r) => (
          <div key={r.label}>
            <Text
              type="secondary"
              style={{
                display: 'block',
                marginBottom: 4,
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {r.label}
            </Text>
            <Text type="secondary" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
              {r.body || '—'}
            </Text>
          </div>
        ))}
      </Space>
    </Card>
  );
}

interface EditorProps {
  template: TemplateForEdit;
  isAdmin: boolean;
}

// Редактор одного шаблона. Монтируется с key=code, поэтому локальный ввод
// инициализируется от эффективных `bodies` выбранного шаблона.
function TemplateEditor({ template, isAdmin }: EditorProps): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const [ru, setRu] = useState<string>(template.bodies['ru'] ?? '');
  const [kk, setKk] = useState<string>(template.bodies['kk'] ?? '');
  const [activeField, setActiveField] = useState<'ru' | 'kk'>('ru');

  const ruRef = useRef<TextAreaRef>(null);
  const kkRef = useRef<TextAreaRef>(null);

  const upsert = useUpsertTemplateOverrideMutation();
  const remove = useDeleteTemplateOverrideMutation();

  const ruEmpty = ru.trim().length === 0;
  // Грязь считаем относительно текущих эффективных bodies. После успешной
  // мутации кэш обновляется и bodies совпадут с локальным вводом → not dirty.
  const baseRu = template.bodies['ru'] ?? '';
  const baseKk = template.bodies['kk'] ?? '';
  const dirty = ru !== baseRu || kk !== baseKk;

  const unknown = useMemo(() => {
    const all = unknownVars(ru, template.variables).concat(unknownVars(kk, template.variables));
    return all.filter((v, i, a) => a.indexOf(v) === i);
  }, [ru, kk, template.variables]);

  // Вставка `{var}` в активное поле по позиции курсора; при недоступном DOM
  // (jsdom) — дописываем в конец с пробелом-разделителем.
  const insertVar = (name: string): void => {
    const token = `{${name}}`;
    const field = activeField;
    const value = field === 'ru' ? ru : kk;
    const setter = field === 'ru' ? setRu : setKk;
    const ref = field === 'ru' ? ruRef : kkRef;
    const textarea = ref.current?.resizableTextArea?.textArea;
    if (textarea && typeof textarea.selectionStart === 'number') {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd ?? start;
      setter(value.slice(0, start) + token + value.slice(end));
    } else {
      const sep = value && !/\s$/.test(value) ? ' ' : '';
      setter(value + sep + token);
    }
  };

  const handleSave = (): void => {
    if (ruEmpty) return;
    const bodies = { ru: ru.trim(), ...(kk.trim() ? { kk: kk.trim() } : {}) };
    upsert.mutate(
      { code: template.code, bodies },
      {
        onSuccess: (data) => {
          setRu(data.bodies['ru'] ?? '');
          setKk(data.bodies['kk'] ?? '');
          void message.success(t('templates.editor.saved'));
        },
        onError: () => {
          void message.error(t('templates.editor.save_error'));
        },
      },
    );
  };

  const handleReset = (): void => {
    remove.mutate(template.code, {
      onSuccess: (data) => {
        setRu(data.bodies['ru'] ?? '');
        setKk(data.bodies['kk'] ?? '');
        void message.success(t('templates.editor.reset_done'));
      },
      onError: () => {
        void message.error(t('templates.editor.reset_error'));
      },
    });
  };

  const saveDisabled = !isAdmin || ruEmpty || !dirty;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        title={
          <Space direction="vertical" size={0}>
            <Text strong>{templateDisplayName(t, template.code)}</Text>
            <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
              {template.code}
            </Text>
          </Space>
        }
        extra={
          <Space size={8}>
            <CategoryTag category={template.category} />
            <StateTag overridden={template.is_overridden} />
          </Space>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {!isAdmin && <Alert type="info" showIcon message={t('templates.editor.employee_note')} />}

          {/* Русский — обязателен */}
          <div>
            <Space size={8} style={{ marginBottom: 6 }}>
              <Text
                strong
                style={{ fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase' }}
              >
                {t('templates.editor.body_ru')}
              </Text>
              <Text style={{ fontSize: 11, color: '#9C2B3F', fontWeight: 600 }}>
                {t('templates.editor.required')}
              </Text>
            </Space>
            <TextArea
              ref={ruRef}
              value={ru}
              disabled={!isAdmin}
              onChange={(e) => setRu(e.target.value)}
              onFocus={() => setActiveField('ru')}
              autoSize={{ minRows: 4 }}
              showCount
              status={ruEmpty ? 'error' : undefined}
              data-testid="template-ru"
            />
            {ruEmpty && (
              <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                {t('templates.editor.ru_required_error')}
              </Text>
            )}
          </div>

          {/* Қазақша — опционально */}
          <div>
            <Space size={8} style={{ marginBottom: 6 }}>
              <Text
                strong
                style={{ fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase' }}
              >
                {t('templates.editor.body_kk')}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t('templates.editor.optional')}
              </Text>
            </Space>
            <TextArea
              ref={kkRef}
              value={kk}
              disabled={!isAdmin}
              onChange={(e) => setKk(e.target.value)}
              onFocus={() => setActiveField('kk')}
              autoSize={{ minRows: 4 }}
              showCount
              placeholder={t('templates.editor.kk_placeholder')}
              data-testid="template-kk"
            />
          </div>

          {unknown.length > 0 && (
            <Alert
              type="warning"
              showIcon
              data-testid="template-unknown-vars"
              message={t('templates.editor.unknown_vars', {
                vars: unknown.map((v) => `{${v}}`).join(', '),
              })}
            />
          )}

          {/* Доступные переменные — клик вставляет {var} */}
          {template.variables.length > 0 && (
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                {t('templates.editor.variables_hint')}
              </Text>
              <Space wrap>
                {template.variables.map((v) => (
                  <Button
                    key={v}
                    size="small"
                    disabled={!isAdmin}
                    className="sl-mono"
                    onClick={() => insertVar(v)}
                    data-testid={`template-var-${v}`}
                  >
                    {`{${v}}`}
                  </Button>
                ))}
              </Space>
            </div>
          )}

          <Space>
            <Button
              type="primary"
              disabled={saveDisabled}
              loading={upsert.isPending}
              onClick={handleSave}
              data-testid="template-save"
            >
              {t('templates.editor.save')}
            </Button>
            {template.is_overridden && (
              <Button
                danger
                disabled={!isAdmin}
                loading={remove.isPending}
                onClick={handleReset}
                data-testid="template-reset"
              >
                {t('templates.editor.reset')}
              </Button>
            )}
          </Space>

          {upsert.error && <ErrorAlert error={upsert.error} />}
          {remove.error && <ErrorAlert error={remove.error} />}
        </Space>
      </Card>

      <SystemBodyBlock template={template} />
    </Space>
  );
}

interface TemplateRowProps {
  template: TemplateForEdit;
  selected: boolean;
  onClick: () => void;
}

function TemplateRow({ template, selected, onClick }: TemplateRowProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`template-row-${template.code}`}
      style={{
        display: 'flex',
        gap: 6,
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        padding: '12px 16px',
        cursor: 'pointer',
        background: selected ? 'var(--mist)' : 'transparent',
        border: 0,
        borderLeft: selected ? '3px solid var(--accent)' : '3px solid transparent',
        borderBottom: '1px solid var(--hairline)',
        width: '100%',
      }}
    >
      <Space size={8} style={{ width: '100%' }}>
        <Text strong style={{ flex: 1 }}>
          {templateDisplayName(t, template.code)}
        </Text>
        <CategoryTag category={template.category} />
      </Space>
      <Space size={8} style={{ width: '100%' }}>
        <Text type="secondary" className="sl-mono" style={{ fontSize: 11, flex: 1 }}>
          {template.code}
        </Text>
        <StateTag overridden={template.is_overridden} />
      </Space>
    </button>
  );
}

interface TemplatesTabProps {
  isAdmin: boolean;
}

// Вкладка «Шаблоны сообщений» (M6): мастер-деталь. Слева — список системных
// кодов с состоянием, справа — редактор override текущей оптики.
export function TemplatesTab({ isAdmin }: TemplatesTabProps): JSX.Element {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<CategoryFilter>('all');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const query = useTemplatesForEditQuery(filter === 'all' ? undefined : filter);
  const items = useMemo(() => query.data?.items ?? [], [query.data]);

  const selected = items.find((tpl) => tpl.code === selectedCode) ?? null;
  const overriddenCount = items.filter((tpl) => tpl.is_overridden).length;

  return (
    <div>
      <Space
        align="center"
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}
        wrap
      >
        <Text type="secondary">
          {t('templates.editor.override_summary', {
            overridden: overriddenCount,
            total: items.length,
          })}
        </Text>
        <Select<CategoryFilter>
          value={filter}
          onChange={setFilter}
          style={{ width: 200 }}
          data-testid="template-filter"
          options={[
            { value: 'all', label: t('templates.editor.filter_all') },
            { value: 'transactional', label: t('templates.category.transactional') },
            { value: 'marketing', label: t('templates.category.marketing') },
          ]}
        />
      </Space>

      {query.isLoading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      ) : query.error ? (
        <ErrorAlert error={query.error} />
      ) : items.length === 0 ? (
        <Empty description={t('templates.empty')} />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={10}>
            <Card styles={{ body: { padding: 0 } }}>
              {items.map((tpl) => (
                <TemplateRow
                  key={tpl.code}
                  template={tpl}
                  selected={tpl.code === selectedCode}
                  onClick={() => setSelectedCode(tpl.code)}
                />
              ))}
            </Card>
          </Col>
          <Col xs={24} md={14}>
            {selected ? (
              <TemplateEditor key={selected.code} template={selected} isAdmin={isAdmin} />
            ) : (
              <Card>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('templates.editor.pick_hint')}
                />
              </Card>
            )}
          </Col>
        </Row>
      )}
    </div>
  );
}
